// Editorial "about this collection" layout: two alternating image+text sections
// followed by a left-ranged rich-text block and the highlights list.
//
// Replaces the generic seo-copy block on collections that have long-form copy
// worth laying out properly. The content lives in ./collection-stories, so this
// file owns layout and typography only.
import type { ReactNode } from "react";
import { LocaleLink } from "@/components/LocaleLink";
import { useT } from "@/lib/i18n";

const H2 = "font-display text-3xl leading-[1.1] md:text-4xl";
// Rich-text sections are siblings of the media sections, not children of them, so
// they are h2 as well — they just carry a smaller type scale to keep the long-form
// block quieter than the two media rows above it.
const RICH_H2 = "mt-8 font-display text-xl leading-tight text-foreground first:mt-0 md:text-2xl";
const PARA = "text-pretty text-[15px] leading-[1.75] text-foreground/80 md:text-base";

/** Inline link styling for use inside story copy. */
export const STORY_LINK =
  "text-terracotta underline decoration-terracotta/40 underline-offset-2 hover:decoration-terracotta";

/** One paragraph's inline content — plain text, or text with inline links. */
type Paragraph = ReactNode;

export type StoryMedia = {
  heading: string;
  paragraphs: Paragraph[];
  image: string;
  imageAlt: string;
};

export type StoryBlock = {
  heading?: string;
  paragraphs: Paragraph[];
};

export type StoryContent = {
  /** Alternating image+text rows. The first puts the image right, the second left. */
  media: StoryMedia[];
  /** Long-form copy below the media rows. */
  rich: StoryBlock[];
};

// Two explicit tracks — a flexible text column and a fixed 400px image column —
// rather than spans on the page's 12-column grid, whose 64px gutters consume
// 704px on gutters alone and leave a wide trench between text and image. The
// 1040px band puts the text at ~560px (≈68 characters) with an 80px gap, and
// sits between the 1500px product grid above and the 896px rich-text block below.
const MEDIA_ROW = "mx-auto grid max-w-[1040px] gap-10 lg:items-center lg:gap-20";

// The source photos are tall portraits. Unconstrained they render ~680x850 on a
// wide desktop, roughly three times the height of the text beside them, so the
// width is capped to keep the crop a calm companion to the text rather than a
// hero image. At lg the parent grid supplies a fixed 400px track, so the cap only
// applies to the stacked layouts below it.
function ImageBlock({ src, alt }: { src: string; alt: string }) {
  return (
    <div className="mx-auto aspect-[4/5] w-full max-w-[300px] overflow-hidden rounded-sm bg-muted sm:max-w-[360px] lg:max-w-none">
      <img
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        sizes="(min-width: 1024px) 400px, (min-width: 640px) 360px, 300px"
        className="h-full w-full object-cover"
      />
    </div>
  );
}

function Paragraphs({ items }: { items: Paragraph[] }) {
  return (
    <div className="mt-5 space-y-5">
      {items.map((p, i) => (
        <p key={i} className={PARA}>
          {p}
        </p>
      ))}
    </div>
  );
}

// The text always comes first in the DOM so the stacked layout reads
// heading -> text -> image on every row; lg:order-first moves the image back to
// the left column on the odd rows once the two tracks exist.
function MediaRow({ media, index }: { media: StoryMedia; index: number }) {
  const imageLeft = index % 2 === 1;
  return (
    <div
      className={`${MEDIA_ROW} ${imageLeft ? "lg:grid-cols-[400px_1fr]" : "lg:grid-cols-[1fr_400px]"}`}
    >
      <div>
        <h2 className={H2}>{media.heading}</h2>
        <Paragraphs items={media.paragraphs} />
      </div>
      <div className={imageLeft ? "lg:order-first" : undefined}>
        <ImageBlock src={media.image} alt={media.imageAlt} />
      </div>
    </div>
  );
}

export function CollectionStory({
  content,
  bullets,
}: {
  content: StoryContent;
  bullets: string[];
}) {
  const { t } = useT();

  return (
    <section className="border-t border-border bg-cream/40">
      <div className="mx-auto max-w-[1500px] px-6 py-20 md:px-10 md:py-28">
        <div className="space-y-24 md:space-y-32">
          {content.media.map((media, i) => (
            <MediaRow key={media.heading} media={media} index={i} />
          ))}
        </div>

        {/* Rich text: the block stays centred on the page, the text ranges left. */}
        <div className="mx-auto mt-24 max-w-4xl text-left md:mt-32">
          {content.rich.map((block, i) => (
            <div key={i} className={i === 0 ? undefined : "mt-8"}>
              {block.heading && <h2 className={RICH_H2}>{block.heading}</h2>}
              <Paragraphs items={block.paragraphs} />
            </div>
          ))}
        </div>

        {bullets.length > 0 && (
          <div className="mx-auto mt-16 max-w-4xl border-t border-border/60 pt-8">
            <p className="eyebrow text-center">{t("collection.highlights")}</p>
            <ul className="mt-5 grid gap-3 sm:grid-cols-2">
              {bullets.map((b) => (
                <li
                  key={b}
                  className="flex items-start gap-3 text-sm leading-relaxed text-foreground/85"
                >
                  <span aria-hidden className="mt-[0.55rem] h-1 w-3 flex-shrink-0 bg-terracotta" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-10 flex flex-wrap justify-center gap-3">
          <LocaleLink
            to="/collections"
            className="inline-flex items-center gap-2 rounded-full border border-foreground/30 px-5 py-2.5 text-xs uppercase tracking-[0.2em] text-foreground hover:bg-foreground hover:text-background"
          >
            {t("collection.browseOther")}
          </LocaleLink>
          <LocaleLink
            to="/visit"
            className="inline-flex items-center gap-2 rounded-full bg-foreground px-5 py-2.5 text-xs uppercase tracking-[0.2em] text-background hover:bg-terracotta"
          >
            {t("topbar.book")}
          </LocaleLink>
        </div>
      </div>
    </section>
  );
}
